import React from 'react';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import classNames from 'classnames';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import { FDSecondaryActionIconButton, FDStack, FDTitleText } from '@facet-wealth/design';
import { GoalItem } from 'Views/Page/Content/Templates/goalsPage/components/GoalItem';
import { noop } from 'Helpers/PropTypes';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import LinkOff from '@material-ui/icons/LinkOff';
import { getBorderRadius } from 'Components/utils';

const useStyles = buildUseStyles('GoalItemGroup', ((theme) => ({
  root: {
    backgroundColor: 'transparent',
    width: '100%',
    position: 'relative',
  },
  dragging: {
    backgroundColor: 'white',
  },
  isGrouped: {
    border: `1px dashed ${theme.palette.primary.main}`,
  },
  iconButton: {
    position: 'absolute',
    right: -20,
    top: -20,
    zIndex: 1,
  },
  iconButtonIcon: {
    background: '#007197',
    color: theme.palette.common.white,
    fontSize: '.75em',
    padding: '3px',
    ...getBorderRadius(theme.radius.full, { all: true }),
  },
})));

// enabling disabling delete, (passing is grouped to item?)
// handles breaking group
const GoalItemGroup = ({
  index,
  resourceGroup,
  combineEnabled = false,
  disabled = false,
  isFirst = false,
  isLast = false,
  isDragging = false,
  onReorderClick = noop,
  onBreakGroup = noop,
  onDelete = noop,
}) => {
  const classes = useStyles();
  const isGrouped = resourceGroup.length > 1;
  const itemNumber = index + 1;

  const NumberOrDragHandle = disabled ? <FDTitleText size={'lg'} value={itemNumber} /> : <FDSecondaryActionIconButton size={'lg'} icon={'dragIndicator'} color={'textSecondary'} />;

  if (resourceGroup.length === 0) {
    return null;
  }
  return (
    <Paper
      className={classNames(classes.root, { [classes.dragging]: isDragging, [classes.isGrouped]: isGrouped })}
      rounded
      elevation={isDragging ? 2 : null}
    >
      <FDStack direction={'row'} alignItems={'center'} width={'100%'}>
        <FDStack minWidth={48} alignItems={'center'} justifyContent={'center'}>
          {NumberOrDragHandle}
        </FDStack>
        <FDStack direction={'column'} width={'100%'}>
          {resourceGroup.map(resource => (
            <GoalItem
              key={resource.id()}
              resource={resource}
              reprioritizing={!disabled}
              isGrouped={isGrouped}
              onDelete={onDelete}
            />
          ))}
        </FDStack>
        {!disabled && (
          <FDStack direction={'row'} alignItems={'center'}>
            <FDStack direction={'column'} justifyContent={'center'} alignItems={'center'}>
              <FDSecondaryActionIconButton
                icon={'arrowDropUp'}
                disabled={isFirst}
                onClick={() => onReorderClick(resourceGroup[0].id(), -1)}
              />
              <FDSecondaryActionIconButton
                icon={'arrowDropDown'}
                disabled={isLast}
                onClick={() => onReorderClick(resourceGroup[0].id(), 1)}
              />
            </FDStack>
          </FDStack>
        )}
        {isGrouped && combineEnabled && (
          <div className={classes.iconButton}>
            <IconButton
              classes={{ icon: classes.iconButtonIcon }}
              icon={LinkOff}
              onClick={() => onBreakGroup(resourceGroup[0].id())}
              title={'Break Group'}
            />
          </div>
        )}
      </FDStack>
    </Paper>
  );
};

GoalItemGroup.propTypes = {
  /** Index position of the resource in the list */
  index: PropTypes.number.isRequired,
  /** resourceGroup object */
  resourceGroup: PropTypes.arrayOf(PropTypes.instanceOf(fr.Resource)).isRequired,
  /** Disabld sorting */
  disabled: PropTypes.bool,
  /** Is true if the item is the first in the list */
  isFirst: PropTypes.bool,
  /** Is true if the item is the last in the list */
  isLast: PropTypes.bool,
  /** Is true if the item is being dragged */
  isDragging: PropTypes.bool,
  /** Callback function called when an item is reordered */
  onReorderClick: PropTypes.func,
  /** Callback function called when the grouped items are broken */
  onBreakGroup: PropTypes.func,
  /** Callback function called when the item is deleted */
  onDelete: PropTypes.func,
};

export { GoalItemGroup };
